const ACCESS_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";

let cachedAccessToken: string | null = null;
let cachedRefreshToken: string | null = null;

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const readSessionValue = (key: string): string | null => {
  try {
    return getSessionStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeSessionValue = (key: string, value: string): void => {
  try {
    getSessionStorage()?.setItem(key, value);
  } catch {
    return;
  }
};

const removeSessionValue = (key: string): void => {
  try {
    getSessionStorage()?.removeItem(key);
  } catch {
    return;
  }
};

/** One-time migration: drop legacy persistent localStorage tokens. */
const migrateLegacyLocalStorageTokens = (): void => {
  if (typeof window === "undefined") return;

  try {
    const legacyAccess = window.localStorage.getItem(ACCESS_TOKEN_KEY);
    const legacyRefresh = window.localStorage.getItem(REFRESH_TOKEN_KEY);

    if (legacyAccess && !readSessionValue(ACCESS_TOKEN_KEY)) {
      writeSessionValue(ACCESS_TOKEN_KEY, legacyAccess);
    }
    if (legacyRefresh && !readSessionValue(REFRESH_TOKEN_KEY)) {
      writeSessionValue(REFRESH_TOKEN_KEY, legacyRefresh);
    }

    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    return;
  }
};

migrateLegacyLocalStorageTokens();

const loadAccessToken = (): string | null => {
  if (cachedAccessToken !== null) return cachedAccessToken || null;
  cachedAccessToken = readSessionValue(ACCESS_TOKEN_KEY) ?? "";
  return cachedAccessToken || null;
};

const loadRefreshToken = (): string | null => {
  if (cachedRefreshToken !== null) return cachedRefreshToken || null;
  cachedRefreshToken = readSessionValue(REFRESH_TOKEN_KEY) ?? "";
  return cachedRefreshToken || null;
};

export const getHasJWT = (): boolean => !!loadAccessToken();
export const getJWT = (): string | null => loadAccessToken();
export const setJWT = (authToken: string): void => {
  cachedAccessToken = authToken;
  if (authToken) {
    writeSessionValue(ACCESS_TOKEN_KEY, authToken);
  } else {
    removeSessionValue(ACCESS_TOKEN_KEY);
  }
};

export const getHasRefreshToken = (): boolean => !!loadRefreshToken();
export const getRefreshToken = (): string | null => loadRefreshToken();
export const setRefreshToken = (refreshToken: string): void => {
  cachedRefreshToken = refreshToken;
  if (refreshToken) {
    writeSessionValue(REFRESH_TOKEN_KEY, refreshToken);
  } else {
    removeSessionValue(REFRESH_TOKEN_KEY);
  }
};
